/**
 * ================================================================
 * 统一术数规则中心 (Unified Divination Rules Center)
 * ================================================================
 * 本文件是整个项目所有术数引擎的唯一基础规则源，严禁任何引擎重复定义
 * 五行、生克、六亲、旺衰、十二长生等核心规则。
 *
 * 修正历史：
 *   - 原六爻引擎中 getLiuQin 误用 WX_SHENG_ME（谁生我）表示"我生者"，
 *     导致木生火时木被误判为"子孙"（应为"父母"）。本文件已修复。
 *
 * 核心原则：
 *   1. 五行相生：木→火→土→金→水→木（不可逆，单向循环）
 *   2. 五行相克：金→木，木→土，土→水，水→火，火→金
 *   3. 六亲以"日主/世爻"为中心，按"生我→我→我生→克我→我克"定
 *
 * 所有术数模块必须从本文件导入规则，禁止自行定义重复的常量或函数。
 * ================================================================
 */

import {
  type TianGan, type DiZhi, type WuXing,
  TIAN_GAN, DI_ZHI, WU_XING,
  GAN_WX, ZHI_WX,
} from '@/utils/calcTools/zhouyi/types';

// ───────────────────────────────────────────────────────────────
// SECTION 1: 五行常量（唯一来源）
// ───────────────────────────────────────────────────────────────
export const ELEMENTS = WU_XING;
export type Element = WuXing;
export type Gan = TianGan;
export type Zhi = DiZhi;

// ───────────────────────────────────────────────────────────────
// SECTION 2: 五行相生相克（本项目的唯一真值来源）
// ───────────────────────────────────────────────────────────────

/** 五行相生：A 生 B（木生火、火生土...） */
export const ELEMENT_GENERATES: Record<Element, Element> = {
  木: '火',
  火: '土',
  土: '金',
  金: '水',
  水: '木',
};

/** 五行被生：谁生 A（火←木、土←火...）—— ELEMENT_GENERATES 的逆 */
export const ELEMENT_GENERATED_BY: Record<Element, Element> = {
  木: '水',
  火: '木',
  土: '火',
  金: '土',
  水: '金',
};

/** 五行相克：A 克 B（木克土、火克金...） */
export const ELEMENT_OVERCOMES: Record<Element, Element> = {
  木: '土',
  火: '金',
  土: '水',
  金: '木',
  水: '火',
};

/** 五行被克：谁克 A（土←木、金←火...）—— ELEMENT_OVERCOMES 的逆 */
export const ELEMENT_OVERCOME_BY: Record<Element, Element> = {
  木: '金',
  火: '水',
  土: '木',
  金: '火',
  水: '土',
};

/**
 * 五行关系查询（给定两个五行，返回关系方向）
 * @returns 关系类型：'generates' | 'overcomes' | 'generatedBy' | 'overcomeBy' | 'same'
 */
export function getWuxingRelation(
  from: Element,
  to: Element,
): 'generates' | 'overcomes' | 'generatedBy' | 'overcomeBy' | 'same' {
  if (from === to) return 'same';
  if (ELEMENT_GENERATES[from] === to) return 'generates';
  if (ELEMENT_OVERCOMES[from] === to) return 'overcomes';
  if (ELEMENT_GENERATED_BY[from] === to) return 'generatedBy';
  if (ELEMENT_OVERCOME_BY[from] === to) return 'overcomeBy';
  return 'same'; // fallback（理论上不会到达）
}

/**
 * 五行相生相克关系描述（中文）
 */
export function getWuxingRelationText(
  from: Element,
  to: Element,
): { relation: string; direction: string } {
  if (from === to) {
    return { relation: '比和', direction: `${from}气相生相助，同气连枝，整体稳定` };
  }
  if (ELEMENT_GENERATES[from] === to) {
    return { relation: '相生', direction: `${from}生${to}，能量流通，生机勃勃` };
  }
  if (ELEMENT_GENERATED_BY[from] === to) {
    return { relation: '被生', direction: `${from}被${to}生，获得滋养，根基稳固` };
  }
  if (ELEMENT_OVERCOMES[from] === to) {
    return { relation: '相克', direction: `${from}克${to}，以力制胜，主动掌控` };
  }
  if (ELEMENT_OVERCOME_BY[from] === to) {
    return { relation: '被克', direction: `${from}被${to}克，受制受压，宜以柔化解` };
  }
  return { relation: '相冲', direction: `${from}与${to}气场不合，需借通关调和` };
}

// ───────────────────────────────────────────────────────────────
// SECTION 3: 五行配色与图标（唯一来源）
// ───────────────────────────────────────────────────────────────

export const ELEMENT_COLORS: Record<Element, string> = {
  木: '#7BC67E',
  火: '#D14B45',
  土: '#A9744F',
  金: '#D4AF37',
  水: '#4A90D9',
};

export const ELEMENT_ICONS: Record<Element, string> = {
  木: 'ri-plant-line',
  火: 'ri-fire-line',
  土: 'ri-landscape-line',
  金: 'ri-medal-line',
  水: 'ri-drop-line',
};

export const ELEMENT_COLOR_NAMES: Record<Element, string> = {
  木: '绿色、青色',
  火: '红色、橙色、紫色',
  土: '黄色、棕色、米色',
  金: '白色、银色、金色',
  水: '黑色、蓝色、深灰',
};

export const ELEMENT_NUMBERS: Record<Element, string> = {
  木: '3、8（木数）',
  火: '2、7（火数）',
  土: '5、0（土数）',
  金: '4、9（金数）',
  水: '1、6（水数）',
};

export const ELEMENT_DIRECTIONS: Record<Element, string> = {
  木: '东方、东南方',
  火: '南方',
  土: '中央、西南、东北',
  金: '西方、西北方',
  水: '北方',
};

export const ELEMENT_PLACES: Record<Element, string> = {
  木: '山林地带、东方城市',
  火: '阳光充足的南方城市',
  土: '平原、内陆城市',
  金: '西方城市、商业发达地区',
  水: '近水之地、北方或滨海城市',
};

// ───────────────────────────────────────────────────────────────
// SECTION 4: 六亲定义（周易/六爻专用，已修复原误用问题）
// ───────────────────────────────────────────────────────────────

export const LIU_QIN_NAMES = ['父母', '官鬼', '兄弟', '妻财', '子孙'] as const;
export type LiuQin = typeof LIU_QIN_NAMES[number];

/**
 * 计算六亲（以世爻/日干的五行为中心）
 * 修正：原引擎误用 WX_SHENG_ME（谁生我）表示"我生者"，已改为 ELEMENT_GENERATES
 *
 * 规则（以日主/世爻五行为中心）：
 *   - 生我者 → 父母
 *   - 同我者 → 兄弟
 *   - 我生者 → 子孙
 *   - 我克者 → 妻财
 *   - 克我者 → 官鬼
 *
 * @param centerWX 日主/世爻的五行
 * @param targetWX 目标爻/天干的五行
 */
export function getLiuQin(centerWX: Element, targetWX: Element): LiuQin {
  if (ELEMENT_GENERATED_BY[centerWX] === targetWX) return '父母'; // 谁生我 = 父母
  if (targetWX === centerWX) return '兄弟';                     // 同我 = 兄弟
  if (ELEMENT_GENERATES[centerWX] === targetWX) return '子孙';  // 我生谁 = 子孙（修正！）
  if (ELEMENT_OVERCOMES[centerWX] === targetWX) return '妻财';  // 我克谁 = 妻财
  if (ELEMENT_OVERCOME_BY[centerWX] === targetWX) return '官鬼'; // 谁克我 = 官鬼
  return '兄弟'; // fallback（理论上不会到达）
}

/**
 * 六亲详细含义
 */
export const LIU_QIN_MEANINGS: Record<LiuQin, { brief: string; icon: string; color: string }> = {
  父母: {
    brief: '父母主庇护、文书、长辈，代表一切生我助我之力',
    icon: 'ri-award-line',
    color: '#B79AC5',
  },
  官鬼: {
    brief: '官鬼主约束、压力、官职、病灾，代表一切克我之力',
    icon: 'ri-sword-line',
    color: '#D14B45',
  },
  兄弟: {
    brief: '兄弟主竞争、朋友、同辈，代表一切同我之力',
    icon: 'ri-team-line',
    color: '#4A90D9',
  },
  妻财: {
    brief: '妻财主财富、感情、下属，代表一切我克之力',
    icon: 'ri-treasure-map-line',
    color: '#E8A020',
  },
  子孙: {
    brief: '子孙主福德、快乐、创意、下属，代表一切我生之力',
    icon: 'ri-lightbulb-line',
    color: '#7BC67E',
  },
};

// ───────────────────────────────────────────────────────────────
// SECTION 5: 十神定义（八字专用）
// ───────────────────────────────────────────────────────────────

export type Shishen =
  | '比肩' | '劫财'
  | '食神' | '伤官'
  | '偏财' | '正财'
  | '七杀' | '正官'
  | '偏印' | '正印'
  | '日主';

/** 天干阴阳 */
export const STEM_YIN_YANG: Record<Gan, 'yang' | 'yin'> = {
  甲: 'yang', 乙: 'yin', 丙: 'yang', 丁: 'yin',
  戊: 'yang', 己: 'yin', 庚: 'yang', 辛: 'yin',
  壬: 'yang', 癸: 'yin',
};

/**
 * 计算十神（以日主天干为基准）
 * 天干类型兼容 TianGan（周易）和 Stem（八字），实质是同一组字符串
 * @param dayStem 日主天干
 * @param otherStem 被推算的天干
 */
export function getShishen(dayStem: Gan, otherStem: Gan): Shishen {
  if (dayStem === otherStem) return '比肩';

  const dayEl = GAN_WX[dayStem] as Element;
  const otherEl = GAN_WX[otherStem] as Element;
  const dayYY = STEM_YIN_YANG[dayStem];
  const otherYY = STEM_YIN_YANG[otherStem];
  const sameYY = dayYY === otherYY;

  if (dayEl === otherEl) return sameYY ? '比肩' : '劫财';
  if (ELEMENT_GENERATES[dayEl] === otherEl) return sameYY ? '食神' : '伤官';
  if (ELEMENT_OVERCOMES[dayEl] === otherEl) return sameYY ? '偏财' : '正财';
  if (ELEMENT_GENERATES[otherEl] === dayEl) return sameYY ? '偏印' : '正印';
  if (ELEMENT_OVERCOMES[otherEl] === dayEl) return sameYY ? '七杀' : '正官';

  return '比肩';
}

export const SHISHEN_ICONS: Record<Shishen | '日主', string> = {
  比肩: 'ri-user-line',
  劫财: 'ri-team-line',
  食神: 'ri-lightbulb-line',
  伤官: 'ri-fire-line',
  偏财: 'ri-treasure-map-line',
  正财: 'ri-money-cny-circle-line',
  七杀: 'ri-sword-line',
  正官: 'ri-scales-line',
  偏印: 'ri-book-open-line',
  正印: 'ri-award-line',
  日主: 'ri-focus-2-line',
};

export const SHISHEN_NATURE: Record<Shishen, 'ji' | 'zhong' | 'xiong'> = {
  比肩: 'zhong',
  劫财: 'zhong',
  食神: 'ji',
  伤官: 'zhong',
  偏财: 'ji',
  正财: 'ji',
  七杀: 'zhong',
  正官: 'ji',
  偏印: 'zhong',
  正印: 'ji',
  日主: 'zhong',
};

// ───────────────────────────────────────────────────────────────
// SECTION 6: 喜用神基础逻辑（八字/紫微通用）
// ───────────────────────────────────────────────────────────────

/**
 * 获取身强时的喜用神（克泄耗方向）
 * @param dayEl 日主五行
 * @returns [官杀(克我), 食伤(我生/泄秀), 财星(我克/耗身)]
 */
export function getXiForStrong(dayEl: Element): Element[] {
  return [
    ELEMENT_OVERCOME_BY[dayEl], // 官杀（克我）
    ELEMENT_GENERATES[dayEl],    // 食伤（我生/泄秀）
    ELEMENT_OVERCOMES[dayEl],    // 财星（我克/耗身）
  ];
}

/**
 * 获取身弱时的喜用神（生扶方向）
 * @param dayEl 日主五行
 * @returns [比劫(同我), 印(生我)]
 */
export function getXiForWeak(dayEl: Element): Element[] {
  return [
    dayEl,                     // 比劫（同类帮扶）
    ELEMENT_GENERATED_BY[dayEl], // 印（生我）
  ];
}

/**
 * 获取身强时的忌神（生扶方向 → 忌）
 */
export function getJiForStrong(dayEl: Element): Element[] {
  return [
    dayEl,                     // 比劫（同类）
    ELEMENT_GENERATED_BY[dayEl], // 印（生我）
  ];
}

/**
 * 获取身弱时的忌神（克泄耗方向 → 忌）
 */
export function getJiForWeak(dayEl: Element): Element[] {
  return [
    ELEMENT_OVERCOME_BY[dayEl], // 官杀（克我）
    ELEMENT_GENERATES[dayEl],   // 食伤（泄气）
    ELEMENT_OVERCOMES[dayEl],   // 财星（耗身）
  ];
}

// ───────────────────────────────────────────────────────────────
// SECTION 7: 旺相休囚死（月令五行强弱）
// ───────────────────────────────────────────────────────────────

/**
 * 五行在各月令（月支）的旺相休囚死
 * 旺=4, 相=3, 休=2, 囚=1, 死=0
 *
 * 月支对应五行旺衰：
 *   子=水旺, 丑=土旺, 寅=木旺, 卯=木旺, 辰=土旺, 巳=火旺,
 *   午=火旺, 未=土旺, 申=金旺, 酉=金旺, 戌=土旺, 亥=水旺
 */
export const WANGXIANG_TABLE: Record<Element, Record<string, number>> = {
  木: { 子: 2, 丑: 1, 寅: 4, 卯: 4, 辰: 2, 巳: 1, 午: 0, 未: 0, 申: 1, 酉: 1, 戌: 2, 亥: 3 },
  火: { 子: 0, 丑: 0, 寅: 3, 卯: 3, 辰: 1, 巳: 4, 午: 4, 未: 3, 申: 0, 酉: 0, 戌: 1, 亥: 0 },
  土: { 子: 1, 丑: 4, 寅: 2, 卯: 1, 辰: 4, 巳: 3, 午: 3, 未: 4, 申: 2, 酉: 2, 戌: 4, 亥: 1 },
  金: { 子: 1, 丑: 3, 寅: 0, 卯: 0, 辰: 2, 巳: 1, 午: 0, 未: 1, 申: 4, 酉: 4, 戌: 3, 亥: 2 },
  水: { 子: 4, 丑: 2, 寅: 1, 卯: 2, 辰: 1, 巳: 0, 午: 0, 未: 0, 申: 3, 酉: 3, 戌: 1, 亥: 4 },
};

/** 旺相休囚死 → 系数（0.0~1.0） */
export function getMonthQiCoeff(element: Element, monthBranch: string): number {
  const strength = WANGXIANG_TABLE[element]?.[monthBranch] ?? 2;
  return strength / 4;
}

/** 旺相休囚死 → 中文名 */
export const WANGXIANG_NAMES: Record<number, string> = {
  4: '旺',
  3: '相',
  2: '休',
  1: '囚',
  0: '死',
};

// ───────────────────────────────────────────────────────────────
// SECTION 8: 十二长生标准表（子平法）
// ───────────────────────────────────────────────────────────────

export const CHANG_SHENG_STANDARD: Record<string, Record<string, string>> = {
  甲: { 子: '沐浴', 丑: '冠带', 寅: '临官', 卯: '帝旺', 辰: '衰', 巳: '病', 午: '死', 未: '墓', 申: '绝', 酉: '胎', 戌: '养', 亥: '长生' },
  乙: { 子: '病', 丑: '衰', 寅: '帝旺', 卯: '临官', 辰: '冠带', 巳: '沐浴', 午: '长生', 未: '养', 申: '胎', 酉: '绝', 戌: '墓', 亥: '死' },
  丙: { 子: '胎', 丑: '养', 寅: '长生', 卯: '沐浴', 辰: '冠带', 巳: '临官', 午: '帝旺', 未: '衰', 申: '病', 酉: '死', 戌: '墓', 亥: '绝' },
  丁: { 子: '绝', 丑: '墓', 寅: '死', 卯: '病', 辰: '衰', 巳: '帝旺', 午: '临官', 未: '冠带', 申: '沐浴', 酉: '长生', 戌: '养', 亥: '胎' },
  戊: { 子: '胎', 丑: '养', 寅: '长生', 卯: '沐浴', 辰: '冠带', 巳: '临官', 午: '帝旺', 未: '衰', 申: '病', 酉: '死', 戌: '墓', 亥: '绝' },
  己: { 子: '绝', 丑: '墓', 寅: '死', 卯: '病', 辰: '衰', 巳: '帝旺', 午: '临官', 未: '冠带', 申: '沐浴', 酉: '长生', 戌: '养', 亥: '胎' },
  庚: { 子: '死', 丑: '墓', 寅: '绝', 卯: '胎', 辰: '养', 巳: '长生', 午: '沐浴', 未: '冠带', 申: '临官', 酉: '帝旺', 戌: '衰', 亥: '病' },
  辛: { 子: '长生', 丑: '养', 寅: '胎', 卯: '绝', 辰: '墓', 巳: '死', 午: '病', 未: '衰', 申: '帝旺', 酉: '临官', 戌: '冠带', 亥: '沐浴' },
  壬: { 子: '帝旺', 丑: '衰', 寅: '病', 卯: '死', 辰: '墓', 巳: '绝', 午: '胎', 未: '养', 申: '长生', 酉: '沐浴', 戌: '冠带', 亥: '临官' },
  癸: { 子: '临官', 丑: '冠带', 寅: '沐浴', 卯: '长生', 辰: '养', 巳: '胎', 午: '绝', 未: '墓', 申: '死', 酉: '病', 戌: '衰', 亥: '帝旺' },
};

// ───────────────────────────────────────────────────────────────
// SECTION 9: 天干地支基础数据（重导出自 types.ts，统一入口）
// ───────────────────────────────────────────────────────────────

export { GAN_WX, ZHI_WX, TIAN_GAN, DI_ZHI };
export { GAN_WX as STEM_ELEMENT, ZHI_WX as BRANCH_ELEMENT };

// ───────────────────────────────────────────────────────────────
// SECTION 10: 五行基础属性（供各模块解释文本引用）
// ───────────────────────────────────────────────────────────────

export const ELEMENT_FIXED_ATTRS: Record<Element, {
  symbol: string;
  keyword: string;
  season: string;
  direction: string;
  taste: string;
  emotion: string;
  colorName: string;
  organ: string;
  organRelated: string;
}> = {
  木: {
    symbol: '大树、竹林、花草',
    keyword: '生长 · 创造 · 仁爱',
    season: '春',
    direction: '东方',
    taste: '酸',
    emotion: '怒（激情、冲劲）',
    colorName: '绿色、青色',
    organ: '肝',
    organRelated: '胆、筋、眼',
  },
  火: {
    symbol: '太阳、炉火、烛光',
    keyword: '热情 · 表达 · 领导',
    season: '夏',
    direction: '南方',
    taste: '苦',
    emotion: '喜（热情、喜悦）',
    colorName: '红色、橙色、紫色',
    organ: '心',
    organRelated: '小肠、血脉、舌',
  },
  土: {
    symbol: '大地、山丘、田园',
    keyword: '稳定 · 包容 · 信用',
    season: '四季末',
    direction: '中央',
    taste: '甘甜',
    emotion: '思（思虑、专注）',
    colorName: '黄色、棕色、米色',
    organ: '脾',
    organRelated: '胃、肌肉、口',
  },
  金: {
    symbol: '宝剑、珠宝、金属',
    keyword: '果断 · 纪律 · 精准',
    season: '秋',
    direction: '西方',
    taste: '辛辣',
    emotion: '悲（敏锐、深思）',
    colorName: '白色、银色、金色',
    organ: '肺',
    organRelated: '大肠、皮肤、鼻',
  },
  水: {
    symbol: '江海、雨露、泉水',
    keyword: '智慧 · 灵活 · 洞察',
    season: '冬',
    direction: '北方',
    taste: '咸',
    emotion: '恐（直觉、深度）',
    colorName: '黑色、蓝色、深灰',
    organ: '肾',
    organRelated: '膀胱、骨骼、耳',
  },
};

/** 五行性格特质摘要（供紫微斗数等模块使用） */
export const ELEMENT_PERSONALITY_NOTES: Record<Element, string> = {
  木: '木主仁慈生长，性格温和有韧性，善谋略，重情义。',
  火: '火主热情礼仪，性格急躁有冲劲，行动力强，善表达。',
  土: '土主稳重诚信，性格务实守信，重积累，善包容。',
  金: '金主刚健决断，性格果决有原则，重信义，善取舍。',
  水: '水主智慧灵动，性格灵活善变通，智商高，善应变。',
};

// ───────────────────────────────────────────────────────────────
// SECTION 10b: 六神定义（六爻专用）
// ───────────────────────────────────────────────────────────────

export const LIU_SHEN = ['青龙', '朱雀', '勾陈', '螣蛇', '白虎', '玄武'] as const;
export type LiuShen = typeof LIU_SHEN[number];

export const LIU_SHEN_COLORS: Record<LiuShen, string> = {
  '青龙': '#4A90D9',
  '朱雀': '#D14B45',
  '勾陈': '#E8C39E',
  '螣蛇': '#B79AC5',
  '白虎': '#C0C0C0',
  '玄武': '#7BC67E',
};

// ───────────────────────────────────────────────────────────────
// SECTION 10c: 英文键名五行映射（供 physiognomy 等使用英文键的模块）
// ───────────────────────────────────────────────────────────────

export const ELEMENT_KEY_EN_TO_ZH: Record<string, Element> = {
  wood: '木',
  fire: '火',
  earth: '土',
  metal: '金',
  water: '水',
};

export const ELEMENT_KEY_ZH_TO_EN: Record<Element, string> = {
  木: 'wood',
  火: 'fire',
  土: 'earth',
  金: 'metal',
  水: 'water',
};

export const ELEMENT_COLORS_EN: Record<string, string> = {
  wood: ELEMENT_COLORS['木'],
  fire: ELEMENT_COLORS['火'],
  earth: ELEMENT_COLORS['土'],
  metal: ELEMENT_COLORS['金'],
  water: ELEMENT_COLORS['水'],
};

// ───────────────────────────────────────────────────────────────
// SECTION 11: 世应定位规则（六爻专用）
// ───────────────────────────────────────────────────────────────

/** 世应位置定位（六爻规则） */
export function getShiYingPos(upperIdx: number, lowerIdx: number): { shi: number; ying: number } {
  const isPure = upperIdx === lowerIdx;
  let shi: number;
  if (isPure) shi = 5;
  else {
    const shiMap = [4, 0, 1, 2, 3, 4, 0, 4];
    shi = shiMap[upperIdx] ?? 0;
  }
  return { shi, ying: (shi + 3) % 6 };
}

// ───────────────────────────────────────────────────────────────
// SECTION 12: 五行旺衰判定（六爻纳甲用）
// ───────────────────────────────────────────────────────────────

/**
 * 纳甲五行旺衰判定（以月令地支五行为基准）
 * @param monthZhi 月支
 * @param wx 待判定五行
 */
export function getWangXiang(
  monthZhi: DiZhi,
  wx: Element,
): '旺' | '相' | '休' | '囚' | '死' {
  const season: Record<string, Element> = {
    寅: '木', 卯: '木',
    巳: '火', 午: '火',
    辰: '土', 未: '土', 戌: '土', 丑: '土',
    申: '金', 酉: '金',
    子: '水', 亥: '水',
  };
  const monthWX = season[monthZhi] ?? '木';
  if (wx === monthWX) return '旺';
  if (ELEMENT_GENERATED_BY[wx] === monthWX) return '相';
  if (ELEMENT_OVERCOMES[monthWX] === wx) return '囚';
  if (ELEMENT_OVERCOMES[wx] === monthWX) return '死';
  return '休';
}

// ───────────────────────────────────────────────────────────────
// SECTION 13: 一致性校验工具
// ───────────────────────────────────────────────────────────────

/**
 * 校验五行生克闭环：验证相生/相克关系是否完整闭环
 * @returns 校验结果：空数组表示全部通过
 */
export function validateWuxingRules(): string[] {
  const errors: string[] = [];
  const elements: Element[] = ['木', '火', '土', '金', '水'];

  // 校验相生闭环
  for (const el of elements) {
    const next = ELEMENT_GENERATES[el];
    const after = ELEMENT_GENERATES[next];
    // 走5步应回到起点
    let current = el;
    for (let i = 0; i < 5; i++) {
      current = ELEMENT_GENERATES[current];
    }
    if (current !== el) {
      errors.push(`相生闭环断裂：从${el}出发5步后回到${current}而非${el}`);
    }
  }

  // 校验相克闭环（2步相克 = 1步相生）
  for (const el of elements) {
    let current = el;
    for (let i = 0; i < 2; i++) {
      current = ELEMENT_OVERCOMES[current];
    }
    if (current !== ELEMENT_GENERATES[el]) {
      errors.push(`相克错位：${el}克两次=${current}，应为${ELEMENT_GENERATES[el]}`);
    }
  }

  // 校验 inverse 正确性
  for (const el of elements) {
    if (ELEMENT_GENERATED_BY[ELEMENT_GENERATES[el]] !== el) {
      errors.push(`相生逆映射错误：${el}生${ELEMENT_GENERATES[el]}，但其被生应为${el}，实际=${ELEMENT_GENERATED_BY[ELEMENT_GENERATES[el]]}`);
    }
    if (ELEMENT_OVERCOME_BY[ELEMENT_OVERCOMES[el]] !== el) {
      errors.push(`相克逆映射错误：${el}克${ELEMENT_OVERCOMES[el]}，但其被克应为${el}，实际=${ELEMENT_OVERCOME_BY[ELEMENT_OVERCOMES[el]]}`);
    }
  }

  return errors;
}

/**
 * 校验六亲映射：验证 getLiuQin 的输出是否符合传统定义
 */
export function validateLiuQin(): string[] {
  const errors: string[] = [];
  const cases: { center: Element; target: Element; expect: LiuQin }[] = [
    // 水生木 → 木的父母是水（水 center=木, target=水 → 父母）
    { center: '木', target: '水', expect: '父母' },
    // 木生火 → 火的子孙是木（木 center=木, target=火 → 子孙）
    { center: '木', target: '火', expect: '子孙' },
    // 木同木 → 兄弟
    { center: '木', target: '木', expect: '兄弟' },
    // 金克木 → 木的官鬼是金（木 center=木, target=金 → 官鬼）
    { center: '木', target: '金', expect: '官鬼' },
    // 木克土 → 木的妻财是土（木 center=木, target: '土', expect: '妻财' })
    { center: '木', target: '土', expect: '妻财' },
    // 同理验证其他五行
    { center: '火', target: '木', expect: '父母' },
    { center: '火', target: '土', expect: '子孙' },
    { center: '火', target: '金', expect: '妻财' },
    { center: '火', target: '水', expect: '官鬼' },
    { center: '土', target: '火', expect: '父母' },
    { center: '土', target: '金', expect: '子孙' },
    { center: '土', target: '水', expect: '妻财' },
    { center: '土', target: '木', expect: '官鬼' },
    { center: '金', target: '土', expect: '父母' },
    { center: '金', target: '水', expect: '子孙' },
    { center: '金', target: '木', expect: '妻财' },
    { center: '金', target: '火', expect: '官鬼' },
    { center: '水', target: '金', expect: '父母' },
    { center: '水', target: '木', expect: '子孙' },
    { center: '水', target: '火', expect: '妻财' },
    { center: '水', target: '土', expect: '官鬼' },
  ];

  for (const { center, target, expect } of cases) {
    const actual = getLiuQin(center, target);
    if (actual !== expect) {
      errors.push(`六亲错误：center=${center}, target=${target} → 期望${expect}，实际${actual}`);
    }
  }

  return errors;
}